import { cssVar, Flex, Icon, IconNameT, Box, Text } from '@cad-arc';
import React, { MouseEvent, useState } from 'react';
import { TileUnit } from '../types';
import { FormattedDurationSince } from './formatted-duration-since';
import { Tooltip } from './tooltip';
import { TileTooltip } from './tile-tooltip';
import { observer } from 'mobx-react-lite';
import { ManageUnitsUnitGroupEnum } from '@mark43/cad-api';
import { compact, round } from 'lodash';
import { useEnabledFeatures } from '../hooks/enabled-features/use-enabled-features';
import { useUnitStatusColors } from '../../configurable-colors-sounds/unit-status-colors';

//TODO: create a common base component for this and event tile
export const UnitTile = observer(function UnitTile({
    unit,
    toggleSelect,
    isSelected,
    isFocused,
    isFocusingLeft,
    isFocusingRight,
    hasPrimary = true,
    primaryUnitId,
    setPrimaryUnitIdAndSelectIfNecessary,
    showStaffCount,
    suggestedStaffCount,
}: {
    unit: TileUnit;
    toggleSelect: (unit: TileUnit) => void;
    isSelected: boolean;
    isFocused?: boolean;
    isFocusingLeft?: boolean;
    isFocusingRight?: boolean;
    hasPrimary?: boolean;
    primaryUnitId?: number;
    setPrimaryUnitIdAndSelectIfNecessary?: (unit: TileUnit) => void;
    showStaffCount?: boolean;
    /**
     * The recommendation's crew for this unit, which dispatch will write as its staff count. Pass it only
     * when that will happen (a selected, newly dispatched recommended unit); it replaces the stored count
     * in the staff count caption with a subdued "Suggested" label.
     */
    suggestedStaffCount?: number;
}): React.ReactElement {
    const { staffCountEnabled, closestUnitRecommendation } = useEnabledFeatures();
    const [mouseOver, setMouseOver] = useState<boolean>(false);
    const primaryUnitType = unit.unitTypes[0]?.label;
    const skillsAndEquipment = compact([...unit.skills, ...unit.equipment])
        .map((i) => i.label)
        .join(', ');
    const unitTypeSkillsAndEquipment = compact([primaryUnitType, skillsAndEquipment]).join(': ');

    const totalTravelTime = closestUnitRecommendation ? unit.totalTravelTimeFormatted : '';
    const totalMiles = closestUnitRecommendation ? unit.totalMiles : '';

    let unitClassIcon: IconNameT | undefined;
    switch (unit.unitClass) {
        case 'P':
            unitClassIcon = 'Police';
            break;
        case 'E':
            unitClassIcon = 'EMS';
            break;
        case 'F':
            unitClassIcon = 'Fire';
            break;
    }

    const unitStatusColor = useUnitStatusColors(unit.unitStateId, unit.unitStateGroup);

    return (
        <Tooltip
            overlay={
                <TileTooltip
                    callSign={unit.callSign.label}
                    beat={unit.beat?.label}
                    location={unit.location}
                    statusCode={unit.statusCode}
                    skillsAndEquipment={skillsAndEquipment}
                    unitTypes={unit.unitTypes.map((type) => type.label).join(', ')}
                />
            }
        >
            <Flex
                cursor="pointer"
                opacity={
                    unit.manageUnitGroup === ManageUnitsUnitGroupEnum.UNAVAILABLE.name &&
                    !isSelected
                        ? '0.5'
                        : undefined
                }
                onMouseEnter={() => setMouseOver(true)}
                onMouseLeave={() => setMouseOver(false)}
                onClick={(_: MouseEvent) => {
                    toggleSelect(unit);
                }}
                w="full"
                h="70px"
                py="1"
                padding="0"
                alignItems="center"
                borderRadius="7"
                marginBottom="2"
                border="sm"
                borderColor={
                    isSelected
                        ? cssVar('arc.colors.brand.default')
                        : cssVar('arc.colors.text.tertiary')
                }
                background={
                    isFocused && !isFocusingLeft && !isFocusingRight
                        ? cssVar('arc.colors.brand.emphasisActive')
                        : isSelected
                          ? cssVar('arc.colors.brand.accent')
                          : mouseOver
                            ? cssVar('arc.colors.selected.default')
                            : undefined
                }
                color={
                    isFocused && !isFocusingLeft && !isFocusingRight
                        ? cssVar('arc.colors.brand.emphasisContent')
                        : isSelected
                          ? cssVar('arc.colors.brand.accentContent')
                          : mouseOver
                            ? cssVar('arc.colors.selected.content')
                            : undefined
                }
            >
                <Box
                    display="flex"
                    borderRadius="7"
                    borderRightRadius="0"
                    pr="1"
                    pt="1"
                    minWidth={0}
                    flexGrow={1}
                    h="full"
                    background={
                        isFocused && isFocusingLeft
                            ? cssVar('arc.colors.brand.emphasisActive')
                            : undefined
                    }
                    color={
                        isFocused && isFocusingLeft
                            ? cssVar('arc.colors.brand.emphasisContent')
                            : undefined
                    }
                >
                    <Box width="30px" display="flex" justifyContent="center" flexShrink={0} pt="px">
                        {unitClassIcon && <Icon icon={unitClassIcon} color="inherit" />}
                    </Box>
                    <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0}>
                        <Flex justifyContent="space-between">
                            <Flex gap="1">
                                <Text fontWeight="bold" variant="bodyMd" color="inherit">
                                    {unit.callSign.label}
                                </Text>
                                <Text variant="bodyMd" color="inherit">
                                    {unit.beat?.label}
                                </Text>
                            </Flex>
                            <Flex
                                textTransform="uppercase"
                                background={unitStatusColor.fill}
                                borderRadius="sm"
                                px="1"
                                gap="1"
                            >
                                <Text
                                    fontWeight="bold"
                                    variant="bodyMd"
                                    style={{ color: unitStatusColor.text }}
                                >
                                    {unit.statusCode}
                                </Text>
                                <FormattedDurationSince
                                    date={new Date(unit.statusStartedDateUtc)}
                                    variant="bodyMd"
                                    style={{ color: unitStatusColor.text }}
                                />
                            </Flex>
                        </Flex>
                        <Flex w="100%" justifyContent="space-between" gap="2">
                            <Box>
                                <Text
                                    noOfLines={1}
                                    fontWeight="bold"
                                    variant="bodyMd"
                                    color="inherit"
                                >
                                    {unit.location ?? '\u00A0'}
                                </Text>
                            </Box>
                            <Flex gap={2} whiteSpace="nowrap">
                                {totalTravelTime ? (
                                    <Flex gap={1}>
                                        <Text variant="bodyMd" color="inherit">
                                            ETA:
                                        </Text>
                                        <Text fontWeight="bold" variant="bodyMd" color="inherit">
                                            {totalTravelTime}
                                        </Text>
                                    </Flex>
                                ) : null}
                                {totalMiles ? (
                                    <Text variant="bodyMd" color="inherit">
                                        {round(totalMiles, 2)} mi
                                    </Text>
                                ) : null}
                            </Flex>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                            <Text
                                noOfLines={1}
                                variant="caption"
                                color="inherit"
                                style={{
                                    ...(showStaffCount && staffCountEnabled
                                        ? { maxWidth: '75%' }
                                        : {}),
                                }}
                            >
                                {unitTypeSkillsAndEquipment}
                            </Text>
                            {showStaffCount &&
                                staffCountEnabled &&
                                (suggestedStaffCount !== undefined ? (
                                    <Text
                                        noOfLines={1}
                                        variant="caption"
                                        color="inherit"
                                        isItalic
                                        title="Suggested by the recommendation; saved as the unit's staff count on dispatch"
                                        data-testid="unit-tile-suggested-staff-count"
                                    >
                                        Suggested: {suggestedStaffCount}
                                    </Text>
                                ) : (
                                    <Text noOfLines={1} variant="caption" color="inherit">
                                        Staff Count: {unit.staffCount ?? 0}
                                    </Text>
                                ))}
                        </Flex>
                    </Box>
                </Box>
                {hasPrimary && (
                    <Box
                        cursor="pointer"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (setPrimaryUnitIdAndSelectIfNecessary) {
                                setPrimaryUnitIdAndSelectIfNecessary(unit);
                            }
                        }}
                        w="30px"
                        h="full"
                        display="flex"
                        flexShrink={0}
                        justifySelf="flex-end"
                        justifyContent="center"
                        alignItems="center"
                        borderLeft="sm"
                        borderLeftColor={
                            isSelected
                                ? cssVar('arc.colors.brand.default')
                                : cssVar('arc.colors.text.tertiary')
                        }
                        borderRightRadius="7"
                        background={
                            isFocused && isFocusingRight
                                ? cssVar('arc.colors.brand.emphasisActive')
                                : undefined
                        }
                        color={
                            isFocused && isFocusingRight
                                ? cssVar('arc.colors.brand.emphasisContent')
                                : undefined
                        }
                    >
                        {primaryUnitId === unit.id && (
                            <Text
                                data-testid={`primary-unit-badge-${unit.callSign.label}`}
                                fontWeight="bold"
                                variant="bodyMd"
                                color="inherit"
                            >
                                P
                            </Text>
                        )}
                    </Box>
                )}
            </Flex>
        </Tooltip>
    );
});
